import CtaArrow from "@/components/svg/CtaArrow";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { getTitleComponents } from "@/helpers";
import SubHeader from "@/components/SubHeader";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";

const textComponents = {
  paragraph: ({ children }) => (
    <p className="p-sm md:p-base text-bleu">{children}</p>
  ),
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} target="_blank" className="text-bleu underline underline-offset-2">
      {children}
    </a>
  ),
};

/**
 * @param {import("@prismicio/client").Repeatable<import("@prismicio/client").LinkField>} ctas
 */
const ImageTextCtas = ({ ctas }) => {
  if (!Array.isArray(ctas) || ctas.length === 0) {
    return null;
  }

  const filledCtas = ctas.filter((cta) => isFilled.link(cta));

  if (filledCtas.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-5">
      {filledCtas.map((cta, index) => {
        const isPrimary = cta.variant === "Primary";

        return (
          <PrismicNextLink
            prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className={`cta cta-text flex flex-row gap-5 ${isPrimary
              ? "bg-rouge text-blanc"
              : "text-bleu underline"
              }`}
          >
            {cta.text}
            {isPrimary ? <CtaArrow className={"text-blanc"} /> : null}
          </PrismicNextLink>
        );
      })}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ImageTextSlice} ImageTextSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ImageTextSlice>} ImageTextProps
 * @type {import("react").FC<ImageTextProps>}
 */
const ImageText = ({ slice }) => {
  const { sub_header, title, sub_title, text, ctas, bg_picture } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-y padding-x"
    >
      <div className="framing-lg flex flex-col lg:flex-row gap-10 lg:gap-20">

        <div className="flex flex-col gap-10 md:gap-15 lg:py-15 lg:w-2/3">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3 md:gap-4">
              {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}
              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={getTitleComponents({
                    color: "bleu",
                    highlightColor: "beige",
                  })}
                />
              )}
              {isFilled.richText(sub_title) && (
                <div className="">
                  <PrismicRichText field={sub_title} components={{
                    heading3: ({ children }) => (
                      <h3 className="p-lg-medium md:p-xl-semibold text-bleu">{children}</h3>
                    ),
                  }} />
                </div>
              )}
            </div>

            {isFilled.richText(text) && (
              <div className="flex flex-col gap-2">
                <PrismicRichText field={text} components={textComponents} />
              </div>
            )}
          </div>

          <ImageTextCtas ctas={ctas} />
        </div>
        <div className="lg:w-1/3 relative rounded-[20px] md:rounded-[30px] overflow-hidden">
          <PrismicNextImage
            field={bg_picture}
            fetchPriority="high"
            loading="eager"
            className="lg:absolute lg:absolute-center object-cover w-full h-[300px] lg:h-auto"
            sizes="(max-width: 1024px) 100vw, 33vw"
          />
        </div>
      </div>
    </section >
  );
};

export default ImageText;
